import { useEffect, useState, useCallback } from "react";
import { Lock, Landmark, Clock, X, ArrowUpRight, ArrowDownLeft, Sparkles, Receipt as ReceiptIcon } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useToast } from "../components/Toast";
import { StatusPill } from "../components/StatusPill";

const DEFAULT_MIN = 10;

export default function Cashout() {
  const { user, refresh } = useAuth();
  const toast = useToast();

  const [amount, setAmount] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [receiptModal, setReceiptModal] = useState(null); // { image_base64, ai, created_at, cash_awarded }

  const balance = user?.balance ?? 0;
  const MIN = user?.min_cashout ?? DEFAULT_MIN;
  const isFirstPayout = (user?.cashout_count ?? 0) === 0;
  const hasMinBalance = balance >= MIN;
  const progress = Math.min(balance / MIN, 1);

  const load = useCallback(async () => {
    try {
      const [items, txs] = await Promise.all([
        api.myCashouts(),
        api.walletTransactions().catch(() => []),
      ]);
      setHistory(items);
      setTransactions(txs || []);
    } catch {
      /* non-fatal */
    }
  }, []);

  const openReceipt = async (receipt_id) => {
    try {
      const r = await api.receipt(receipt_id);
      setReceiptModal(r);
    } catch (e) {
      toast.error("Couldn't load that receipt");
    }
  };

  useEffect(() => {
    refresh();
    load();
  }, [refresh, load]);

  // Prefill bank details from user profile once loaded (saved from last payout).
  useEffect(() => {
    if (!user) return;
    if (!sortCode && user.bank_sort_code) setSortCode(formatSortCode(user.bank_sort_code));
    if (!accountNumber && user.bank_account_number) setAccountNumber(user.bank_account_number);
    if (!accountName && user.bank_account_name) setAccountName(user.bank_account_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < MIN) {
      toast.error(`Minimum cashout amount is £${MIN}.`);
      return;
    }
    if (amt > balance) {
      toast.error("Amount exceeds your balance.");
      return;
    }
    const sc = sortCode.replace(/\D/g, "");
    const an = accountNumber.replace(/\D/g, "");
    if (sc.length !== 6) {
      toast.error("Sort code must be 6 digits (e.g. 12-34-56).");
      return;
    }
    if (an.length !== 8) {
      toast.error("Account number must be 8 digits.");
      return;
    }
    if (!accountName.trim()) {
      toast.error("Please enter the account holder name.");
      return;
    }

    setSubmitting(true);
    try {
      await api.createCashout({
        amount: amt,
        sort_code: sc,
        account_number: an,
        account_name: accountName.trim(),
      });
      setAmount("");
      await refresh();
      await load();
      toast.success("Payout requested — funds will land in your bank within 1-10 hours.");
    } catch (err) {
      toast.error(err?.message || "Try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page fade-in">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0 }}>Withdraw</h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 6, marginBottom: 10 }}>
        Withdraw your earned balance directly to your UK bank.
      </p>
      <div
        data-testid="cashout-fee-note"
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: "rgba(31, 174, 94, 0.10)",
          border: "1px solid rgba(31, 174, 94, 0.35)",
          color: "#1FAE5E",
          borderRadius: 999, padding: "6px 12px",
          fontSize: 12, fontWeight: 800, letterSpacing: 0.2,
          marginBottom: 20,
        }}
      >
        <span aria-hidden style={{
          width: 18, height: 18, borderRadius: 9,
          background: "#1FAE5E", color: "#FFFFFF",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 900,
        }}>£</span>
        {isFirstPayout
          ? "First payout from just £10 — no withdrawal fees"
          : "Minimum £20 — no withdrawal fees"}
      </div>

      {/* Balance hero */}
      <div style={{
        borderRadius: 28, background: "var(--burgundy)",
        border: "1px solid var(--burgundy)", padding: 22, marginBottom: 18,
        boxShadow: "0 16px 32px -18px rgba(122, 26, 46, 0.55)",
      }}>
        <div style={{ color: "rgba(255,255,255,0.78)", fontSize: 11, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase" }}>
          Available Balance
        </div>
        <div data-testid="cashout-balance" style={{ color: "#FFFFFF", fontSize: 44, fontWeight: 800, letterSpacing: -1.5, marginTop: 6 }}>
          £{balance.toFixed(2)}
        </div>
        <div style={{ height: 8, background: "rgba(255,255,255,0.18)", borderRadius: 4, overflow: "hidden", marginTop: 14 }}>
          <div style={{ height: "100%", background: "#FFFFFF", width: `${progress * 100}%`, transition: "width .4s ease" }} />
        </div>
        <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, marginTop: 8 }}>
          {hasMinBalance
            ? "You're eligible to cash out!"
            : `£${(MIN - balance).toFixed(2)} more to unlock cash out`}
        </div>
      </div>

      {!hasMinBalance ? (
        <div data-testid="cashout-locked-notice" style={{
          display: "flex", flexDirection: "column", gap: 14,
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 28, padding: 20, marginTop: 16,
        }}>
          <div style={{ display: "flex", gap: 12 }}>
            <Lock size={20} color="var(--primary)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>Minimum £{MIN} required</div>
              <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>
                Keep snapping receipts to top up your wallet. Once you reach £{MIN} you'll be able to cash out.
              </div>
            </div>
          </div>
          <button
            data-testid="cashout-locked-cta"
            type="button"
            onClick={() => toast.error(`You need at least £${MIN} in your wallet to cash out. £${(MIN - balance).toFixed(2)} to go!`)}
            className="btn-primary"
            style={{ width: "100%" }}
          >
            Withdraw
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} style={{
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 28, padding: 20, marginTop: 16,
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Withdraw amount</div>
          <div style={{
            display: "flex", alignItems: "center",
            background: "var(--surface-elev)", border: "1px solid var(--border)",
            borderRadius: 16, padding: "0 16px",
          }}>
            <span style={{ color: "var(--primary)", fontSize: 26, fontWeight: 800, marginRight: 10 }}>£</span>
            <input
              data-testid="cashout-amount-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{
                flex: 1, background: "transparent", color: "var(--text-primary)",
                fontSize: 26, fontWeight: 800, padding: "16px 0", outline: "none", border: "none",
              }}
            />
          </div>
          <button
            data-testid="cashout-max-button"
            type="button"
            onClick={() => setAmount(balance.toFixed(2))}
            style={{ color: "var(--primary)", fontSize: 13, fontWeight: 700, marginTop: 8, padding: 0 }}
          >
            Use max (£{balance.toFixed(2)})
          </button>

          {/* Bank details */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            marginTop: 22, marginBottom: 12,
            fontSize: 15, fontWeight: 800,
          }}>
            <Landmark size={18} color="var(--primary)" />
            Where to send it
          </div>

          <label style={fieldLabelStyle}>Account holder name</label>
          <input
            data-testid="cashout-account-name-input"
            type="text"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder="Full name as it appears on the account"
            autoComplete="name"
            style={inputStyle}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12, marginTop: 12 }}>
            <div>
              <label style={fieldLabelStyle}>Sort code</label>
              <input
                data-testid="cashout-sort-code-input"
                type="text"
                inputMode="numeric"
                value={sortCode}
                onChange={(e) => setSortCode(formatSortCode(e.target.value))}
                placeholder="12-34-56"
                maxLength={8}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={fieldLabelStyle}>Account number</label>
              <input
                data-testid="cashout-account-number-input"
                type="text"
                inputMode="numeric"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="12345678"
                maxLength={8}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            color: "var(--text-secondary)", fontSize: 12,
            marginTop: 14, lineHeight: 1.45,
          }}>
            <Clock size={14} style={{ marginTop: 2, flexShrink: 0 }} color="var(--primary)" />
            <span>
              We send payouts by manual UK bank transfer. Funds usually land in your account within <b>1–10 hours</b>.
            </span>
          </div>

          <button
            data-testid="cashout-submit-button"
            type="submit"
            className="btn-primary"
            disabled={submitting}
            style={{ width: "100%", marginTop: 18 }}
          >
            {submitting ? "Submitting…" : "Payout"}
          </button>
        </form>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Past Requests</div>
          {history.map((h) => (
            <div
              key={h.id}
              data-testid={`cashout-history-${h.id}`}
              style={{
                display: "flex", alignItems: "center",
                background: "var(--surface)", border: "1px solid var(--border)",
                borderRadius: 16, padding: 14, marginTop: 10,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>£{h.amount.toFixed(2)}</div>
                <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>
                  {new Date(h.created_at).toLocaleDateString()}{" "}
                  {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <StatusPill status={h.status} />
            </div>
          ))}
        </div>
      )}
      {transactions.length > 0 && (
        <div style={{ marginTop: 28 }} data-testid="wallet-transactions">
          <div style={{
            display: "flex", alignItems: "baseline", justifyContent: "space-between",
            marginBottom: 12,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Transactions</div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 700 }}>
              Tap a receipt to view it
            </div>
          </div>
          {transactions.map((t) => (
            <TransactionRow key={t.id} tx={t} onOpen={openReceipt} />
          ))}
        </div>
      )}

      {receiptModal && (
        <ReceiptViewerModal
          receipt={receiptModal}
          onClose={() => setReceiptModal(null)}
        />
      )}

    </div>
  );
}

/* ---------- Transaction row ---------- */
function TransactionRow({ tx, onOpen }) {
  const isIn = tx.direction === "in";
  const clickable = tx.kind === "receipt" && !!tx.receipt_id;
  const iconMap = {
    receipt: <ReceiptIcon size={16} color="#1FAE5E" />,
    streak_bonus: <Sparkles size={16} color="#F77A2A" />,
    demo_bonus: <Sparkles size={16} color="#F77A2A" />,
    cashout: <ArrowUpRight size={16} color="var(--accent-pink)" />,
  };
  const bgTint = {
    receipt: "rgba(31,174,94,0.10)",
    streak_bonus: "rgba(247,122,42,0.12)",
    demo_bonus: "rgba(247,122,42,0.12)",
    cashout: "rgba(230,59,90,0.10)",
  };
  const ts = tx.created_at ? new Date(tx.created_at) : null;
  return (
    <div
      data-testid={`wallet-tx-${tx.kind}`}
      onClick={clickable ? () => onOpen(tx.receipt_id) : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 16, padding: 14, marginTop: 10,
        cursor: clickable ? "pointer" : "default",
        transition: "transform 120ms ease",
      }}
    >
      <span style={{
        width: 34, height: 34, borderRadius: 999, flexShrink: 0,
        background: bgTint[tx.kind] || "var(--surface-elev)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {iconMap[tx.kind] || <ArrowDownLeft size={16} color="var(--text-secondary)" />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "var(--navy)" }}>
          {tx.title}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
          {ts ? `${ts.toLocaleDateString()} ${ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"}
        </div>
      </div>
      <div style={{
        fontSize: 16, fontWeight: 800, letterSpacing: -0.3,
        color: isIn ? "#1FAE5E" : "var(--accent-pink)",
        whiteSpace: "nowrap",
      }}>
        {isIn ? "+" : "−"}£{Number(tx.amount).toFixed(2)}
      </div>
    </div>
  );
}

/* ---------- Receipt viewer modal ---------- */
function ReceiptViewerModal({ receipt, onClose }) {
  const ai = receipt.ai || {};
  const brandPretty = {
    mcdonalds: "McDonald's", kfc: "KFC", popeyes: "Popeyes",
  }[ai.brand] || (ai.brand || "Receipt");
  const cash = Number(receipt.cash_awarded || receipt.amount || 0);
  return (
    <div
      data-testid="wallet-receipt-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 260,
        background: "rgba(14, 34, 64, 0.82)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 18, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 400, maxHeight: "92vh", overflowY: "auto",
          background: "var(--surface)", borderRadius: 24,
          padding: "18px 16px 16px", position: "relative",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="wallet-receipt-close"
          style={{
            position: "absolute", top: 10, right: 10,
            width: 32, height: 32, borderRadius: 16,
            background: "var(--surface-soft)", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={16} color="var(--text-secondary)" />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{
            width: 34, height: 34, borderRadius: 999,
            background: "rgba(31,174,94,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ReceiptIcon size={16} color="#1FAE5E" />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--navy)" }}>
              {brandPretty} receipt
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
              {receipt.decided_at ? new Date(receipt.decided_at).toLocaleString() : ""}
            </div>
          </div>
          {cash > 0 && (
            <div style={{
              background: "rgba(31,174,94,0.14)",
              border: "1px solid rgba(31,174,94,0.35)",
              color: "#1FAE5E",
              borderRadius: 999, padding: "6px 12px",
              fontSize: 13, fontWeight: 800,
            }}>
              +£{cash.toFixed(2)}
            </div>
          )}
        </div>
        {receipt.image_base64 ? (
          <img
            src={receipt.image_base64}
            alt="Receipt"
            style={{
              width: "100%", borderRadius: 14, border: "1px solid var(--border)",
              display: "block",
            }}
          />
        ) : (
          <div style={{ color: "var(--text-secondary)", fontSize: 13, padding: 24, textAlign: "center" }}>
            Receipt image not available.
          </div>
        )}
        {ai.total_amount && (
          <div style={{
            marginTop: 10, display: "flex", justifyContent: "space-between",
            fontSize: 13, fontWeight: 700, color: "var(--text-primary)",
            background: "var(--peach-bg-soft)", border: "1px solid var(--peach-border)",
            borderRadius: 12, padding: "10px 14px",
          }}>
            <span>Total spend</span>
            <span>£{Number(ai.total_amount).toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const fieldLabelStyle = {
  display: "block",
  color: "var(--text-secondary)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  background: "var(--surface-elev)",
  border: "1px solid var(--border)",
  borderRadius: 14,
  padding: "12px 14px",
  fontSize: 15,
  fontWeight: 700,
  color: "var(--text-primary)",
  outline: "none",
};

// UK sort code helper — accepts any input, keeps up to 6 digits and inserts
// dashes after every 2 digits for readability. Digits only are sent to backend.
function formatSortCode(v) {
  const digits = (v || "").replace(/\D/g, "").slice(0, 6);
  const groups = [];
  for (let i = 0; i < digits.length; i += 2) groups.push(digits.slice(i, i + 2));
  return groups.join("-");
}
